import React from 'react';
import withStyles from 'isomorphic-style-loader/lib/withStyles';
import { motion } from 'framer-motion';
import { Anchor, Globe, Shield, CreditCard, Star } from 'lucide-react';
import s from './DownloadApp.css';

const content = {
    en: {
        headline: 'Rent Your Boat\nAnywhere in the World',
        subheadline: 'The all-in-one platform for boat rentals. Book instantly, pay securely, explore freely.',
        features: [
            { icon: Anchor, title: 'Boats', desc: 'Yachts, sailboats & more' },
            { icon: Globe, title: '5 Languages', desc: 'Global coverage' },
            { icon: Shield, title: 'Verified', desc: 'Identity & security' },
            { icon: CreditCard, title: '135+ Currencies', desc: 'Crypto accepted' },
        ],
        proof: [
            { value: '5.0', label: 'App Rating' },
            { value: '3', label: 'Platforms' },
            { value: '135+', label: 'Currencies' },
            { value: '24/7', label: 'Support' },
        ],
        ctaText: 'Download Free Now',
        ctaSub: 'Available on iOS and Android',
        appStore: 'App Store',
        appStoreSub: 'Download on the',
        playStore: 'Google Play',
        playStoreSub: 'Get it on',
        footer: '\u00a9 2026 NAVEGA. All rights reserved.'
    },
    es: {
        headline: 'Renta tu Bote\nDesde Cualquier Lugar',
        subheadline: 'La plataforma todo-en-uno para alquiler de botes. Reserva al instante, paga seguro, explora libre.',
        features: [
            { icon: Anchor, title: 'Botes', desc: 'Yates, veleros y mas' },
            { icon: Globe, title: '5 Idiomas', desc: 'Cobertura global' },
            { icon: Shield, title: 'Verificado', desc: 'Identidad y seguridad' },
            { icon: CreditCard, title: '135+ Monedas', desc: 'Crypto aceptado' },
        ],
        proof: [
            { value: '5.0', label: 'Rating App' },
            { value: '3', label: 'Plataformas' },
            { value: '135+', label: 'Monedas' },
            { value: '24/7', label: 'Soporte' },
        ],
        ctaText: 'Descarga Gratis Ahora',
        ctaSub: 'Disponible en iOS y Android',
        appStore: 'App Store',
        appStoreSub: 'Descargar en',
        playStore: 'Google Play',
        playStoreSub: 'Disponible en',
        footer: '\u00a9 2026 NAVEGA. Todos los derechos reservados.'
    }
};

const APPSTORE_URL = 'https://apps.apple.com/us/app/navega-renta-tu-bote/id6742487553';
const PLAYSTORE_URL = 'https://play.google.com/store/apps/details?id=com.navega.app';

const screenshots = [
    { src: '/investors/app-splash.png', alt: 'Navega App' },
    { src: '/investors/app-explore.png', alt: 'Explore' },
    { src: '/investors/app-listings.png', alt: 'Listings' },
    { src: '/investors/app-map.png', alt: 'Map' },
    { src: '/investors/app-trips.png', alt: 'Trips' },
];

/* High-quality Apple SVG icon */
const AppleIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 384 512" fill="currentColor">
        <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-27.1-46.7-42.1-82.9-44.9-34.2-2.8-71.5 20-83.6 20-12.5 0-45.3-18.7-68.5-18.2-35.4.5-68 20.7-86.2 52.6-37.1 64.4-9.5 159.9 26.2 212.4 17.8 25.6 38.7 54.5 66 53.5 26.6-1 36.7-17.2 68.8-17.2 31.8 0 41.2 17.2 68.8 16.7 28.8-.5 46.9-25.7 64.4-51.5 20.3-29.6 28.5-58 29-59.6-.6-.3-55.4-21.2-55.9-84.5zM262.8 60.2c14.2-17.5 24.4-41.4 21.5-65.5-20.3 1.5-44.8 14.3-59.1 31.5-12.8 15.3-24.4 40.2-21.3 63.6 22.6 1.4 45.8-11.6 58.9-29.6z" />
    </svg>
);

/* High-quality Google Play SVG icon */
const PlayStoreIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 512 512" fill="none">
        <path d="M48.2 44.8C42.1 51.2 38.5 61.5 38.5 75.2v361.6c0 13.7 3.6 24 9.7 30.4l1.6 1.5 202.7-202.7v-4.8L49.8 58.5l-1.6-1.5z" fill="#4285F4" />
        <path d="M320 328.4l-67.5-67.5v-4.8L320 188.6l1.5.9 80 45.4c22.8 13 22.8 34.2 0 47.2l-80 45.4-1.5.9z" fill="#FBBC04" />
        <path d="M321.5 327.5L252.5 258.5 48.2 463c7.5 8 19.9 9 33.8 1.3l239.5-136.8z" fill="#EA4335" />
        <path d="M321.5 189.5L82 52.7C68.1 44.9 55.7 46 48.2 54l204.3 204.5 69-69z" fill="#34A853" />
    </svg>
);

// Animation variants
const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.3 } }
};

const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

const scaleIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: 'easeOut' } }
};

class DownloadApp extends React.Component {
    constructor(props) {
        super(props);
        const lang = props.locale && props.locale.startsWith('es') ? 'es' : 'en';
        this.state = { lang };
    }

    render() {
        const { lang } = this.state;
        const t = content[lang];

        // Duplicate screenshots for infinite scroll
        const doubledScreenshots = [...screenshots, ...screenshots];

        return (
            <div className={s.pageWrapper}>
                {/* Animated Ocean Background Elements */}
                <div className={s.oceanWaves}>
                    <div className={`${s.wave}`}></div>
                    <div className={`${s.wave} ${s.wave2}`}></div>
                    <div className={`${s.wave} ${s.wave3}`}></div>
                </div>
                <div className={s.caustics}></div>
                <div className={s.particles}>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                    <div className={s.particle}></div>
                </div>

                {/* ===== HERO ===== */}
                <section className={s.hero}>

                    {/* Logo */}
                    <motion.div
                        className={s.logoContainer}
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        <img
                            src="/navega-logo-white.png"
                            alt="NAVEGA"
                            className={s.logo}
                        />
                    </motion.div>

                    {/* Headline */}
                    <motion.h1
                        className={s.headline}
                        initial="hidden"
                        animate="visible"
                        variants={fadeUp}
                    >
                        {t.headline}
                    </motion.h1>

                    <motion.p
                        className={s.subheadline}
                        initial="hidden"
                        animate="visible"
                        variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: 0.15 } } }}
                    >
                        {t.subheadline}
                    </motion.p>

                    {/* Store Badges - Large Prominent Buttons */}
                    <motion.div
                        className={s.badgesRow}
                        initial="hidden"
                        animate="visible"
                        variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: 0.25 } } }}
                    >
                        <a
                            href={APPSTORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.badgeLink}
                        >
                            <AppleIcon className={s.badgeIcon} />
                            <div className={s.badgeTextGroup}>
                                <span className={s.badgeSubtext}>{t.appStoreSub}</span>
                                <span className={s.badgeMainText}>{t.appStore}</span>
                            </div>
                        </a>
                        <a
                            href={PLAYSTORE_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={s.badgeLink}
                        >
                            <PlayStoreIcon className={s.badgeIcon} />
                            <div className={s.badgeTextGroup}>
                                <span className={s.badgeSubtext}>{t.playStoreSub}</span>
                                <span className={s.badgeMainText}>{t.playStore}</span>
                            </div>
                        </a>
                    </motion.div>

                    {/* App Screenshots - Auto-Scroll Carousel */}
                    <motion.div
                        className={s.showcaseSection}
                        initial="hidden"
                        animate="visible"
                        variants={{ ...fadeUp, visible: { ...fadeUp.visible, transition: { ...fadeUp.visible.transition, delay: 0.35 } } }}
                    >
                        <div className={s.screenshotTrack}>
                            {doubledScreenshots.map((shot, i) => (
                                <div key={i} className={s.screenshotCard}>
                                    <img
                                        src={shot.src}
                                        alt={shot.alt}
                                        className={s.screenshotImg}
                                    />
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    {/* Features Strip */}
                    <motion.div
                        className={s.featuresStrip}
                        initial="hidden"
                        animate="visible"
                        variants={staggerContainer}
                    >
                        {t.features.map((feat, i) => {
                            const IconComp = feat.icon;
                            return (
                                <motion.div
                                    key={i}
                                    className={s.featureCard}
                                    variants={staggerItem}
                                    whileHover={{ y: -3, transition: { duration: 0.2 } }}
                                >
                                    <div className={s.featureIcon}>
                                        <IconComp size={18} />
                                    </div>
                                    <div className={s.featureTitle}>{feat.title}</div>
                                    <div className={s.featureDesc}>{feat.desc}</div>
                                </motion.div>
                            );
                        })}
                    </motion.div>

                    {/* Social Proof */}
                    <motion.div
                        className={s.socialProof}
                        initial="hidden"
                        animate="visible"
                        variants={staggerContainer}
                    >
                        {t.proof.map((item, i) => (
                            <motion.div key={i} className={s.proofItem} variants={staggerItem}>
                                {item.label === 'App Rating' || item.label === 'Rating App' ? (
                                    <div className={s.starsRow}>
                                        {[1, 2, 3, 4, 5].map(n => (
                                            <Star key={n} size={14} fill="#c9a84c" className={s.starIcon} />
                                        ))}
                                    </div>
                                ) : null}
                                <div className={s.proofValue}>{item.value}</div>
                                <div className={s.proofLabel}>{item.label}</div>
                            </motion.div>
                        ))}
                    </motion.div>

                    <hr className={s.divider} />

                    {/* Bottom CTA */}
                    <motion.div
                        className={s.ctaBottom}
                        initial="hidden"
                        animate="visible"
                        variants={scaleIn}
                    >
                        <div className={s.ctaText}>{t.ctaText}</div>
                        <div className={s.ctaSubtext}>{t.ctaSub}</div>
                        <div className={s.ctaBadges}>
                            <a
                                href={APPSTORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={s.ctaBadgeLink}
                            >
                                <AppleIcon className={s.ctaBadgeIcon} />
                                <span className={s.ctaBadgeText}>{t.appStore}</span>
                            </a>
                            <a
                                href={PLAYSTORE_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={s.ctaBadgeLink}
                            >
                                <PlayStoreIcon className={s.ctaBadgeIcon} />
                                <span className={s.ctaBadgeText}>{t.playStore}</span>
                            </a>
                        </div>
                    </motion.div>

                </section>

                {/* Footer */}
                <footer className={s.footer}>
                    <p className={s.footerText}>{t.footer}</p>
                </footer>
            </div>
        );
    }
}

export default withStyles(s)(DownloadApp);
